import { inferBases } from "./baseInference";
import { haversineKm } from "./distance";

const AVG_DRIVE_KMH = 50;
const MAX_SINGLE_DAY_TRIP_KM = 300;
const MAX_CLUSTER_HOURS = 10;

const toNights = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const hasCoords = (p) =>
  p && Number.isFinite(p.lat) && Number.isFinite(p.lon);

export const validateTrip = ({ origin, stops, totalNights } = {}) => {
  const errors = [];
  const warnings = [];
  const safeStops = Array.isArray(stops) ? stops : [];
  const enriched = inferBases({ origin, stops: safeStops });

  // V1 — total nights must match trip duration
  const totalAssigned = safeStops.reduce((s, x) => s + toNights(x && x.nights), 0);
  if (Number.isFinite(totalNights) && totalNights >= 0 && totalAssigned !== totalNights) {
    errors.push({
      id: "V1",
      message: `Total nights (${totalAssigned}) must equal trip duration (${totalNights} ${totalNights === 1 ? "night" : "nights"}).`,
    });
  }


  enriched.forEach((e) => {
    if (e.kind !== "day_trip") return;
    if (!e.baseBefore || !e.baseAfter) {
      errors.push({
        id: "V2",
        stopId: e.id,
        message: `${e.name || "This day-trip stop"} has no valid base — add a stay before or after it, or set a starting location.`,
      });
    }
  });

  enriched.forEach((e) => {
    if (e.kind !== "day_trip") return;
    const before = e.baseBefore;
    if (!hasCoords(before) || !hasCoords(e)) return;
    const km = haversineKm(before.lat, before.lon, e.lat, e.lon);
    if (km != null && km > MAX_SINGLE_DAY_TRIP_KM) {
      warnings.push({
        id: "V4",
        stopId: e.id,
        message: `${e.name} is ~${Math.round(km)} km from ${before.name || "the base"} — that's a long day trip (>${MAX_SINGLE_DAY_TRIP_KM} km one-way).`,
      });
    }
  });

  // V3 — consecutive 0n cluster drive time
  const seenClusters = new Set();
  enriched.forEach((e) => {
    if (e.kind !== "day_trip" || !e.cluster) return;
    if (seenClusters.has(e.cluster.id)) return;
    if (e.cluster.size <= 1) return;
    seenClusters.add(e.cluster.id);

    const memberStops = enriched.filter((x) => e.cluster.memberIds.includes(x.id));
    const path = [e.baseBefore, ...memberStops, e.baseAfter].filter(Boolean);
    let totalKm = 0;
    let aborted = false;
    for (let i = 0; i < path.length - 1; i += 1) {
      const a = path[i];
      const b = path[i + 1];
      if (!hasCoords(a) || !hasCoords(b)) {
        aborted = true;
        break;
      }
      totalKm += haversineKm(a.lat, a.lon, b.lat, b.lon) || 0;
    }
    if (aborted) return;
    const hours = totalKm / AVG_DRIVE_KMH;
    if (hours > MAX_CLUSTER_HOURS) {
      warnings.push({
        id: "V3",
        clusterId: e.cluster.id,
        message: `Combined day visit (${memberStops.map((s) => s.name).join(", ")}) needs ~${Math.round(hours)}h of driving — split across days for comfort.`,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    enriched,
  };
};

export default validateTrip;
